import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GripVertical, Plus, User, Euro, Building, Phone } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

export default function AdminPipelinePage() {
  const [stages, setStages] = useState([]);
  const [pipelineData, setPipelineData] = useState({});
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/admin-crm/pipeline`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStages(data.stages);
        setPipelineData(data.data);
      }
    } catch (error) {
      console.error('Error fetching pipeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, prospect, stageId) => {
    setDraggedItem({ prospect, fromStage: stageId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, toStageId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.fromStage === toStageId) {
      setDraggedItem(null);
      return;
    }

    const token = localStorage.getItem('admin_token');
    const { prospect, fromStage } = draggedItem;

    // Optimistic update
    setPipelineData(prev => {
      const newData = { ...prev };
      newData[fromStage] = newData[fromStage].filter(p => p.id !== prospect.id);
      newData[toStageId] = [...(newData[toStageId] || []), { ...prospect, pipeline_stage: toStageId }];
      return newData;
    });

    try {
      const response = await fetch(
        `${API_URL}/api/admin-crm/pipeline/move?prospect_id=${prospect.id}&new_stage=${toStageId}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Prospect déplacé');
      } else {
        fetchPipeline();
        toast.error('Erreur lors du déplacement');
      }
    } catch (error) {
      fetchPipeline();
      toast.error('Erreur lors du déplacement');
    }

    setDraggedItem(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getOfferPrice = (offer) => {
    const prices = { essentiel: 499, business: 949, premium: 1499 };
    return prices[offer] || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Calculate totals
  const totals = {};
  stages.forEach(stage => {
    const prospects = pipelineData[stage.id] || [];
    totals[stage.id] = {
      count: prospects.length,
      value: prospects.reduce((sum, p) => sum + getOfferPrice(p.interested_offer), 0)
    };
  });

  return (
    <div className="space-y-6" data-testid="admin-pipeline-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline Commercial</h1>
          <p className="text-gray-500">Suivi de tes opportunités</p>
        </div>
      </div>

      {/* Pipeline Kanban */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {stages.map((stage) => {
            const prospects = pipelineData[stage.id] || [];
            const stageTotal = totals[stage.id];

            return (
              <div
                key={stage.id}
                className="w-72 flex-shrink-0"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Stage Header */}
                <div
                  className="rounded-t-xl p-4 text-white"
                  style={{ backgroundColor: stage.color }}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{stage.name}</h3>
                    <span className="bg-white/20 text-xs px-2 py-1 rounded-full">
                      {stageTotal.count}
                    </span>
                  </div>
                  {stageTotal.value > 0 && (
                    <p className="text-sm text-white/80 mt-1">{formatCurrency(stageTotal.value)}</p>
                  )}
                </div>

                {/* Cards Container */}
                <div className="bg-gray-100 rounded-b-xl p-3 min-h-[400px] space-y-3">
                  {prospects.map((prospect) => (
                    <motion.div
                      key={prospect.id}
                      layout
                      draggable
                      onDragStart={(e) => handleDragStart(e, prospect, stage.id)}
                      className={`bg-white rounded-lg p-4 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${
                        draggedItem?.prospect.id === prospect.id ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-blue-600 font-semibold text-sm">
                                {prospect.name?.charAt(0)}
                              </span>
                            </div>
                            <p className="font-medium text-gray-900 truncate">
                              {prospect.name}
                            </p>
                          </div>

                          {prospect.company_name && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-2">
                              <Building className="w-3 h-3" />
                              {prospect.company_name}
                            </p>
                          )}

                          {prospect.phone && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <Phone className="w-3 h-3" />
                              {prospect.phone}
                            </p>
                          )}

                          {prospect.interested_offer && (
                            <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                              <span className="text-xs text-purple-600 capitalize">
                                {prospect.interested_offer}
                              </span>
                              <span className="text-xs font-semibold text-gray-900">
                                {formatCurrency(getOfferPrice(prospect.interested_offer))}
                              </span>
                            </div>
                          )}

                          {prospect.diagnostic_score && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-500">Score diagnostic</span>
                                <span className="font-medium text-purple-600">{prospect.diagnostic_score}%</span>
                              </div>
                              <div className="h-1.5 bg-gray-200 rounded-full mt-1">
                                <div
                                  className="h-full bg-purple-500 rounded-full"
                                  style={{ width: `${prospect.diagnostic_score}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {prospects.length === 0 && (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      Aucun prospect
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <h4 className="font-semibold text-gray-900 mb-3">Étapes du pipeline</h4>
        <div className="flex flex-wrap gap-4">
          {stages.map((stage) => (
            <div key={stage.id} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
              <span className="text-sm text-gray-600">{stage.name}</span>
              <span className="text-xs text-gray-400">({totals[stage.id]?.count || 0})</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
